import { useState, useEffect, useRef, useCallback } from 'react';
import { Video, Camera, Heart, CheckCircle, Square, RotateCcw, Upload, ArrowLeft, Play } from 'lucide-react';
import styles from '../../styles/LandingPage.module.css';
import recordingImg from '../../assets/images/landing/kiosk-recording-demo.jpg';
import reviewImg from '../../assets/images/landing/kiosk-review-demo.jpg';

const SCREENS = [
  { id: 'welcome', label: 'Welcome Screen' },
  { id: 'form', label: 'Guest Details' },
  { id: 'recording', label: 'Recording' },
  { id: 'review', label: 'Review' },
  { id: 'success', label: 'Thank You' },
];

const CAPTIONS = {
  welcome: 'Guests see the welcome screen with your event name, date, and options to record a video or take a photo.',
  form: 'They optionally enter their name — no account or app required.',
  recording: 'The camera opens and they record their heartfelt message in HD.',
  review: 'They preview their recording and confirm — or retake if they want.',
  success: 'Done! The video uploads in the background and the kiosk resets for the next guest.',
};

function WelcomeScreen({ onNext }) {
  return (
    <div className={styles.kioskScreen}>
      <div className={styles.kioskWelcome}>
        <div className={styles.kioskHeart}>
          <Heart className={styles.kioskHeartIcon} fill="currentColor" />
        </div>
        <h3 className={styles.kioskEventName}>Sarah & John's Wedding</h3>
        <p className={styles.kioskEventMeta}>March 15, 2026 &bull; The Grand Ballroom</p>
        <p className={styles.kioskWelcomeMsg}>Share a memory for the happy couple!</p>
        <div className={styles.kioskButtons}>
          <button className={styles.kioskBtnPrimary} onClick={() => onNext('video')}>
            <Video size={18} />
            <span>Record a Video</span>
          </button>
          <button className={styles.kioskBtnSecondary} onClick={() => onNext('photo')}>
            <Camera size={18} />
            <span>Take a Photo</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function FormScreen({ onNext }) {
  return (
    <div className={styles.kioskScreen}>
      <div className={styles.kioskFormInner}>
        <div className={styles.kioskFormIcon}>
          <Video size={28} />
        </div>
        <h3 className={styles.kioskFormTitle}>Before we start...</h3>
        <p className={styles.kioskFormSub}>Add your name (optional), then record your message.</p>
        <div className={styles.kioskField}>
          <label>Your Name (Optional)</label>
          <div className={styles.kioskInput}>Jane Doe</div>
        </div>
        <div className={styles.kioskFormActions}>
          <button className={styles.kioskBtnGhost}>
            <ArrowLeft size={16} />
            Back
          </button>
          <button className={styles.kioskBtnPrimary} onClick={onNext}>
            Start Recording
          </button>
        </div>
      </div>
    </div>
  );
}

function RecordingScreen({ elapsed, onStop }) {
  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;
  const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  return (
    <div className={styles.kioskScreen}>
      <div className={styles.kioskRecording}>
        <div className={styles.kioskCameraArea}>
          <img src={recordingImg} alt="Guest recording" className={styles.kioskCameraImg} />
          <div className={styles.kioskRecIndicator}>
            <span className={styles.kioskRecDot} />
            <span>REC</span>
            <span className={styles.kioskTimer}>{timeStr}</span>
          </div>
        </div>
        <button className={styles.kioskStopBtn} onClick={onStop}>
          <Square size={20} fill="currentColor" />
          <span>Stop Recording</span>
        </button>
      </div>
    </div>
  );
}

function ReviewScreen({ onNext }) {
  return (
    <div className={styles.kioskScreen}>
      <div className={styles.kioskReview}>
        <div className={styles.kioskPreviewArea}>
          <img src={reviewImg} alt="Video preview" className={styles.kioskPreviewImg} />
          <div className={styles.kioskPlayOverlay}>
            <Play size={28} fill="currentColor" />
          </div>
        </div>
        <div className={styles.kioskReviewActions}>
          <button className={styles.kioskBtnGhost}>
            <RotateCcw size={16} />
            Retake
          </button>
          <button className={styles.kioskBtnPrimary} onClick={onNext}>
            <Upload size={16} />
            Upload
          </button>
        </div>
      </div>
    </div>
  );
}

function SuccessScreen({ onRestart }) {
  return (
    <div className={styles.kioskScreen}>
      <div className={styles.kioskSuccess}>
        <div className={styles.kioskCheckmark}>
          <CheckCircle size={48} />
        </div>
        <h3 className={styles.kioskThankYou}>Thank You!</h3>
        <p className={styles.kioskSuccessMsg}>Your message has been uploaded.</p>
        <p className={styles.kioskSuccessSub}>Sarah & John will treasure your video forever.</p>
        <button className={styles.kioskBtnPrimary} onClick={onRestart}>
          Record Another Message
        </button>
      </div>
    </div>
  );
}

export default function KioskDemo() {
  const [currentScreen, setCurrentScreen] = useState(0);
  const [fade, setFade] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);
  const autoAdvanceRef = useRef(null);

  const goTo = useCallback((index) => {
    setFade(false);
    setTimeout(() => {
      setCurrentScreen(index);
      setElapsed(0);
      setFade(true);
    }, 200);
  }, []);

  const next = useCallback(() => {
    goTo(Math.min(currentScreen + 1, SCREENS.length - 1));
  }, [currentScreen, goTo]);

  // Recording timer
  useEffect(() => {
    if (SCREENS[currentScreen].id === 'recording') {
      timerRef.current = setInterval(() => {
        setElapsed(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [currentScreen]);

  // Auto-advance after idle
  useEffect(() => {
    if (autoAdvanceRef.current) clearTimeout(autoAdvanceRef.current);

    const screenId = SCREENS[currentScreen].id;
    if (screenId === 'recording') {
      autoAdvanceRef.current = setTimeout(() => next(), 4000);
    } else if (screenId === 'success') {
      autoAdvanceRef.current = setTimeout(() => goTo(0), 6000);
    } else {
      autoAdvanceRef.current = setTimeout(() => {
        if (currentScreen < SCREENS.length - 1) next();
      }, 7000);
    }

    return () => {
      if (autoAdvanceRef.current) clearTimeout(autoAdvanceRef.current);
    };
  }, [currentScreen, next, goTo]);

  const scrollToInquiry = () => {
    document.getElementById('inquiry')?.scrollIntoView({ behavior: 'smooth' });
  };

  const renderScreen = () => {
    switch (SCREENS[currentScreen].id) {
      case 'welcome':
        return <WelcomeScreen onNext={() => next()} />;
      case 'form':
        return <FormScreen onNext={() => next()} />;
      case 'recording':
        return <RecordingScreen elapsed={elapsed} onStop={() => next()} />;
      case 'review':
        return <ReviewScreen onNext={() => next()} />;
      case 'success':
        return <SuccessScreen onRestart={() => goTo(0)} />;
      default:
        return null;
    }
  };

  return (
    <section id="demo" className={styles.problem}>
      <div className={styles.container}>
        <h2 className={styles.sectionTitle}>See What Your Guests Experience</h2>
        <p className={styles.sectionSubtitle}>
          An interactive preview of the MomentBox kiosk. Tap through to see the full flow.
        </p>

        <div className={styles.demoWrapper}>
          {/* Tablet frame */}
          <div className={styles.tabletFrame}>
            <div className={styles.tabletBezel}>
              <div className={styles.tabletCamera} />
            </div>
            <div className={`${styles.tabletScreen} ${fade ? styles.tabletScreenVisible : styles.tabletScreenHidden}`}>
              {renderScreen()}
            </div>
          </div>

          {/* Step indicators */}
          <div className={styles.stepIndicators}>
            {SCREENS.map((screen, i) => (
              <button
                key={screen.id}
                className={`${styles.stepDot} ${i === currentScreen ? styles.stepDotActive : ''}`}
                onClick={() => goTo(i)}
                aria-label={screen.label}
              />
            ))}
          </div>

          {/* Caption */}
          <p className={`${styles.demoCaption} ${fade ? styles.tabletScreenVisible : styles.tabletScreenHidden}`}>
            <span className={styles.demoCaptionStep}>Step {currentScreen + 1}:</span>{' '}
            {CAPTIONS[SCREENS[currentScreen].id]}
          </p>
        </div>

        <div className={styles.demoCta}>
          <button className={styles.ctaButton} onClick={scrollToInquiry}>
            Get This For Your Event
          </button>
        </div>
      </div>
    </section>
  );
}
